import { toast } from "react-toastify";
import { request } from "../../config/helpers/axios-instance";
import { DELIVERY_PARTNER_IDS_MAP } from "./constants";

const getUserPatientAddresses = async (mapUserPatientId) => {
    try {
        const response = await request({
            url: `address/mapUserPatient/${mapUserPatientId}`,
            method: 'GET',
        })

        if (response.data && response.data.success) {
            return response.data?.data?.rows;
        }
    } catch (error) {
        toast.warn("Couldn't get addresses.")
    }

    return [];
}

const getServiceability = async (deliveryPartnerId, data, containsLiquid = false, isCod = false) => {
    try {
        switch (deliveryPartnerId) {
            case (DELIVERY_PARTNER_IDS_MAP.SHIPROCKET): {
                const { pickup, delivery, weight, cod } = data;
                if (!delivery || !pickup || (cod !== 0 && cod !== 1) || !weight) {
                    toast.warn("Invalid parameters.");
                    return null;
                }

                const response = await request({
                    url: `delivery/serviceability?pickup=${pickup}&delivery=${delivery}&weight=${weight}&cod=${cod}&deliveryPartnerId=${deliveryPartnerId}&containsLiquid=${containsLiquid}&isCod=${isCod}`,
                    method: 'GET',
                })

                if (response.data && response.data.success) {
                    return response.data?.data;
                }
                break;
            }
            case (DELIVERY_PARTNER_IDS_MAP.DTDC): {
                const { pickup, delivery } = data;
                if (!delivery || !pickup) {
                    toast.warn("Invalid parameters.");
                    return null;
                }

                const response = await request({
                    url: `delivery/serviceability?pickup=${pickup}&delivery=${delivery}&deliveryPartnerId=${deliveryPartnerId}&containsLiquid=${containsLiquid}&isCod=${isCod}`,
                    method: 'GET',
                })
                if (response.data && response.data.success) {
                    return response.data?.data;
                }
                break;
            }
            default: {
                toast.warn("Couldn't get serviceability.")
                return null;
            }
        }
        return null;
    } catch (error) {
        toast.warn("Couldn't get serviceability.")
        return null;
    }
}

const validateDTDCDeliveryDetails = (data) => {
    if (!data) {
        toast.warn("Please fill all the fields.");
        return false;
    }

    const requiredFields = [
        'orderId',
        'billingAddressId',
        'deliveryPartnerId'
    ];

    for (const field of requiredFields) {
        if (!data[field]) {
            toast.warn(`Please fill ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    // Validate package details
    if (!data.packageDetails) {
        toast.warn("Package details are required");
        return false;
    }

    const requiredPackageFields = [
        'units',
        // 'length',
        // 'breadth',
        // 'height',
        // 'weight',
        'subTotal',
    ];

    for (const field of requiredPackageFields) {
        if (!data.packageDetails[field] && data.packageDetails[field] !== 0) {
            toast.warn(`Please fill package ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    // Validate numeric fields
    // const numericFields = ['units', 'length', 'breadth', 'height', 'weight', 'subTotal'];
    const numericFields = ['units', 'subTotal'];
    for (const field of numericFields) {
        if (isNaN(data.packageDetails[field]) || data.packageDetails[field] <= 0) {
            toast.warn(`Invalid value for package ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    return true;
}

const validateDeliveryDetails = (data) => {
    if (!data) {
        toast.warn("Please fill all the fields.");
        return false;
    }

    const requiredFields = [
        'orderId',
        'billingAddressId',
        'shippingAddressId',
        'deliveryPartnerId'
    ];

    for (const field of requiredFields) {
        if (!data[field]) {
            toast.warn(`Please fill ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    // Validate package details
    if (!data.packageDetails) {
        toast.warn("Package details are required");
        return false;
    }

    const requiredPackageFields = [
        'name',
        'sku',
        'units',
        'sellingPrice',
        'length',
        'breadth',
        'height',
        'weight',
        'subTotal'
    ];

    for (const field of requiredPackageFields) {
        if (!data.packageDetails[field] && data.packageDetails[field] !== 0) {
            toast.warn(`Please fill package ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    // Validate numeric fields
    const numericFields = ['units', 'length', 'breadth', 'height', 'weight', 'subTotal'];
    for (const field of numericFields) {
        if (isNaN(data.packageDetails[field]) || data.packageDetails[field] <= 0) {
            toast.warn(`Invalid value for package ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    return true;
}

const validateCourierDetails = (data) => {
    if (!data) {
        toast.warn("Please fill all the fields.");
        return false;
    }

    const requiredFields = [
        'courierId',
    ];

    for (const field of requiredFields) {
        if (!data[field]) {
            toast.warn(`Please fill ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    return true;
}


const validateDtdcCourierDetails = (data) => {
    if (!data) {
        toast.warn("Please fill all the fields.");
        return false;
    }

    const requiredFields = [
        'serviceType',
    ];

    for (const field of requiredFields) {
        if (!data[field]) {
            toast.warn(`Please fill ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    return true;
}

const getDeliveryPartners = async () => {
    try {
        const response = await request({
            url: 'delivery/deliveryPartners',
            method: 'GET',
        });

        if (response.data?.success) {
            return response.data.data || [];
        }
    } catch (error) {
        toast.warn("Couldn't load delivery partners.");
    }

    return [];
};

const validateManualDeliveryDetails = (data, isSelf) => {
    if (!data) {
        toast.warn("Please fill all the fields.");
        return false;
    }

    const requiredFields = [
        'orderId',
        'billingAddressId',
        'deliveryPartnerId',
    ];

    for (const field of requiredFields) {
        if (!data[field] && data[field] !== 0) {
            toast.warn(`Please fill ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    // AWB / reference number and tracking link are optional: manual courier vendors often
    // issue the AWB after pickup, and both can be added later from the Delivery Queue
    // ("Add AWB / Tracking"). Only a link that IS entered has to look like one.
    if (!isSelf && data.trackingLink && !/^https?:\/\/\S+$/i.test(String(data.trackingLink).trim())) {
        toast.warn("Tracking link must start with http:// or https://");
        return false;
    }

    if (!data.packageDetails) {
        toast.warn("Package details are required");
        return false;
    }

    const requiredPackageFields = ['units', 'subTotal'];
    for (const field of requiredPackageFields) {
        if (!data.packageDetails[field] && data.packageDetails[field] !== 0) {
            toast.warn(`Please fill package ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    const numericFields = ['units', 'subTotal'];
    for (const field of numericFields) {
        if (isNaN(data.packageDetails[field]) || data.packageDetails[field] <= 0) {
            toast.warn(`Invalid value for package ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
            return false;
        }
    }

    return true;
};

const createManualDelivery = async (data) => {
    try {
        const response = await request({
            url: 'delivery/manual',
            method: 'POST',
            data,
        });

        if (response.data?.success) {
            toast.success("Manual delivery created successfully.");
            return response.data;
        }

        toast.warn(response.data?.err || "Couldn't create manual delivery.");
        return null;
    } catch (error) {
        toast.warn("Couldn't create manual delivery.");
        return null;
    }
};

const schedulePickup = async (data) => {
    try {
        const response = await request({
            url: `delivery/`,
            method: 'POST',
            data
        })
        if (response.data && response.data.success) {
            toast.success("Pickup scheduled successfully.")
            return response.data;
        }
        toast.warn("Couldn't schedule pickup.")
        return null;
    } catch (error) {
        toast.warn("Couldn't schedule pickup.")
        return null;
    }
}

const generateLabel = async (deliveryId) => {
    if (!deliveryId) {
        toast.warn("Invalid ID")
        return;
    }

    try {
        const response = await request({
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            url: `delivery/generateLabel/${deliveryId}`,
        });

        if (response.data && response.data.success) {
            toast.success("Label Generated");
            window.open(response.data?.data?.labelUrl, '_blank');
        } else {
            toast.error("Error generating label");
        }
    } catch (error) {
        toast.error("Error generating label");
    }
}

const isManualDelivery = (delivery) => delivery?.additionalDetails?.isManual === true;

const getDeliveryTrackingLink = (delivery) => delivery?.additionalDetails?.trackingLink || '';

const copyTrackingLink = async (trackingLink) => {
    if (!trackingLink) {
        toast.warn("No tracking link available");
        return;
    }

    try {
        await navigator.clipboard.writeText(trackingLink);
        toast.success("Tracking link copied");
    } catch (error) {
        toast.warn("Could not copy tracking link");
    }
};

export {
    getUserPatientAddresses,
    getServiceability,
    getDeliveryPartners,
    isManualDelivery,
    getDeliveryTrackingLink,
    copyTrackingLink,
    validateCourierDetails,
    validateDtdcCourierDetails,
    validateDeliveryDetails,
    validateDTDCDeliveryDetails,
    validateManualDeliveryDetails,
    createManualDelivery,
    schedulePickup,
    generateLabel
}